/**
 * PodTube FeedService - Fetches YouTube feeds with fallback resilience and offline caching
 */

import { store } from "./store.js";
import { formatDuration, SHORT_MAX_SECONDS } from "./ui.js";

// Pre-seeded fallback data for offline / instant first render
const SEED_EPISODES = [
  {
    id: "ylVwRn0cFTk",
    videoId: "ylVwRn0cFTk",
    channelId: "UCAAGdEk4YbbkTJZ0IqaAljA",
    channelTitle: "Jaime Rodríguez de Santiago",
    title: "#281 Juegos de estatus: matones, santos y cracks",
    description:
      "En el episodio de hoy de Kaizen hablamos sobre cómo los juegos de estatus dominan nuestras relaciones, trabajo y vidas...",
    published: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    thumbnail: "https://i.ytimg.com/vi/ylVwRn0cFTk/hqdefault.jpg",
    author: "Jaime Rodríguez de Santiago",
    isShort: false,
    duration: 1098,
  },
  {
    id: "tengounplan_seed1",
    videoId: "bY0f0c0c0c0", // placeholder or real id
    channelId: "UCJFP-5V2-0BFeVmKifG0H_Q",
    channelTitle: "Tengo un Plan",
    title: "Analista Financiero explica dónde Invertir en 2026 ¡El Reset ha Comenzado!",
    description:
      "Entrevista en profundidad con los mejores analistas sobre las oportunidades de inversión, economía macro y toma de control financiero.",
    published: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
    thumbnail: "https://i.ytimg.com/vi_webp/ylVwRn0cFTk/hqdefault.webp",
    author: "Tengo un Plan",
    isShort: false,
    duration: 5400,
  },
  {
    id: "nadiesabenada_seed1",
    videoId: "cZ1g1g1g1g1",
    channelId: "UCWU8fcAN3X8hjMR9k0rbV5g",
    channelTitle: "Nadie Sabe Nada",
    title: "Un podcast de extraterrestres | @NadieSabeNada",
    description:
      "Andreu Buenafuente y Berto Romero improvisan durante una hora sobre las mayores absurdeces del universo y preguntas del público.",
    published: new Date(Date.now() - 1000 * 60 * 60 * 72).toISOString(),
    thumbnail: "https://i.ytimg.com/vi_webp/ylVwRn0cFTk/hqdefault.webp",
    author: "Nadie Sabe Nada",
    isShort: false,
    duration: 3600,
  },
];

// LocalStorage feed cache TTL: 30 minutes
const FEED_CACHE_TTL_MS = 30 * 60 * 1000;

function lsGetFeed(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Legacy format (plain array) — treat as stale to force refresh
    if (Array.isArray(parsed)) {
      localStorage.removeItem(key);
      return null;
    }
    if (parsed && Array.isArray(parsed.items) && parsed.timestamp) {
      if (Date.now() - parsed.timestamp < FEED_CACHE_TTL_MS) {
        return parsed.items;
      }
      // Expired: remove
      localStorage.removeItem(key);
      return null;
    }
  } catch {}
  return null;
}

function lsSetFeed(key, items) {
  try {
    localStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), items }));
  } catch {}
}

class FeedService {
  constructor() {
    this.memoryCache = new Map();
    /** @type {number|null} Timestamp of last successful full feed sync */
    this.lastSyncAt = null;
  }

  /**
   * Fetch feed for a single channel
   */
  async fetchChannelFeed(channelId, forceRefresh = false, extended = false) {
    if (!channelId) return [];

    const cacheKey = extended ? `${channelId}:extended` : channelId;

    // Check memory cache
    if (!forceRefresh && this.memoryCache.has(cacheKey)) {
      return this.memoryCache.get(cacheKey);
    }

    let items = [];

    // Strategy 1: Backend API (/api/feed) with resilient YouTube RSS + HTML fallback
    try {
      const url = `/api/feed?channelId=${encodeURIComponent(channelId)}${extended ? "&extended=true" : ""}${forceRefresh ? "&refresh=true" : ""}`;
      const res = await fetch(url, {
        signal: AbortSignal.timeout(20000),
      });
      if (res.ok) {
        const data = await res.json();
        if (data && Array.isArray(data.items)) {
          items = data.items;
        }
      }
    } catch (err) {
      console.warn(`Feed API fetch error for ${channelId}:`, err.message);
    }

    // Strategy 2: Offline LocalStorage cache or Seeded data
    if (items.length === 0) {
      const cached = lsGetFeed(`feed_${cacheKey}`) || lsGetFeed(`feed_${channelId}`);
      if (cached) {
        items = cached;
      } else {
        items = SEED_EPISODES.filter((e) => e.channelId === channelId);
      }
    }

    // Enrich items with known durations from store and short-video heuristics
    const enriched = items.map((item) => {
      const knownDur = store.getVideoDuration(item.videoId) || item.duration;
      if (item.duration && !store.getVideoDuration(item.videoId)) {
        store.saveVideoDuration(item.videoId, item.duration);
      }
      const isShort = Boolean(
        item.isShort ||
        (knownDur && knownDur < SHORT_MAX_SECONDS) ||
        /#shorts?\b/i.test(item.title) ||
        /#shorts?\b/i.test(item.description)
      );

      const canonicalChannelId =
        channelId.startsWith("rss:") || channelId.startsWith("http")
          ? channelId
          : channelId.startsWith("UC")
            ? channelId
            : `UC${channelId}`;
      const itemId = item.id || item.videoId || item.guid;
      return {
        ...item,
        id: itemId,
        videoId: item.videoId || itemId,
        channelId: canonicalChannelId,
        duration: knownDur || item.duration || null,
        isShort,
      };
    });

    if (enriched.length > 0) {
      this.memoryCache.set(cacheKey, enriched);
      lsSetFeed(`feed_${cacheKey}`, enriched);
    }

    return enriched;
  }

  /**
   * Client-side DOMParser for Atom and RSS feeds
   */
  parseXmlString(xmlString, fallbackChannelId) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlString, "text/xml");

      const isRss = doc.querySelector("rss, channel") !== null;
      if (isRss) {
        const channelTitle = doc.querySelector("channel > title")?.textContent || "Podcast";
        const channelImg =
          doc.querySelector("channel > image > url")?.textContent ||
          doc.querySelector("itunes\\:image, image")?.getAttribute("href") ||
          "";
        const items = Array.from(doc.querySelectorAll("item"));
        return items.map((it, idx) => {
          const title = it.querySelector("title")?.textContent || "";
          const link = it.querySelector("link")?.textContent || "";
          const guid = it.querySelector("guid")?.textContent || link || String(idx);
          let safeId = `rss_${idx}`;
          try {
            safeId = `rss_${btoa(unescape(encodeURIComponent(guid))).slice(0, 24)}`;
          } catch {}
          const enc = it.querySelector("enclosure");
          const audioUrl = enc?.getAttribute("url") || null;
          const pubDate = it.querySelector("pubDate")?.textContent;
          const published = pubDate ? new Date(pubDate).toISOString() : new Date().toISOString();
          const desc = it.querySelector("description, summary")?.textContent || "";
          const itunesImg = it.querySelector("itunes\\:image")?.getAttribute("href");
          const durStr = it.querySelector("itunes\\:duration")?.textContent;
          let duration = null;
          if (durStr) {
            const parts = durStr.split(":").map(Number);
            if (parts.length === 2 && !parts.some(isNaN)) duration = parts[0] * 60 + parts[1];
            if (parts.length === 3 && !parts.some(isNaN))
              duration = parts[0] * 3600 + parts[1] * 60 + parts[2];
          }
          return {
            id: safeId,
            videoId: safeId,
            guid,
            audioUrl,
            channelId: fallbackChannelId,
            channelTitle,
            title,
            description: desc.replace(/<[^>]+>/g, " ").slice(0, 300),
            published,
            author: it.querySelector("itunes\\:author, creator")?.textContent || channelTitle,
            thumbnail: itunesImg || channelImg,
            isShort: duration !== null ? duration < SHORT_MAX_SECONDS : false,
            duration,
            link,
            sourceType: /substack/i.test(fallbackChannelId) ? "substack" : "rss",
          };
        });
      }

      const channelTitle = doc.querySelector("feed > title")?.textContent || "Canal";
      const channelId =
        doc.querySelector("feed > yt\\:channelId, channelId")?.textContent || fallbackChannelId;
      const entries = Array.from(doc.querySelectorAll("entry"));

      return entries.map((entry) => {
        const videoId =
          entry.querySelector("yt\\:videoId, videoId")?.textContent ||
          entry.querySelector("id")?.textContent.replace("yt:video:", "") ||
          "";
        const title = entry.querySelector("title")?.textContent || "";
        const description =
          entry.querySelector("media\\:description, description")?.textContent || "";
        const published = entry.querySelector("published")?.textContent || new Date().toISOString();
        const author = entry.querySelector("author > name")?.textContent || channelTitle;
        const thumbnail =
          entry.querySelector("media\\:thumbnail")?.getAttribute("url") ||
          `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

        return {
          id: videoId,
          videoId,
          channelId,
          channelTitle,
          title,
          description,
          published,
          author,
          thumbnail,
          isShort: /#shorts?\b/i.test(title) || /#shorts?\b/i.test(description),
          duration: null,
        };
      });
    } catch (e) {
      console.error("Error parsing XML in browser:", e);
      return [];
    }
  }

  /**
   * Fetch all subscribed feeds and sort chronologically
   */
  async fetchAllFeeds(channels, forceRefresh = false, extended = false) {
    const promises = channels
      .filter((c) => c.enabled !== false)
      .map((c) => this.fetchChannelFeed(c.id, forceRefresh, extended));

    const results = await Promise.allSettled(promises);
    let allItems = [];

    for (const r of results) {
      if (r.status === "fulfilled" && Array.isArray(r.value)) {
        allItems.push(...r.value);
      }
    }

    // Deduplicate by item id or videoId
    const seen = new Set();
    const uniqueItems = [];
    for (const item of allItems) {
      const key = item.id || item.videoId || item.guid;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueItems.push(item);
      }
    }

    // Sort by publication date desc
    uniqueItems.sort((a, b) => new Date(b.published).getTime() - new Date(a.published).getTime());

    // Record sync timestamp if we got real data
    if (uniqueItems.length > 0) {
      this.lastSyncAt = Date.now();
    }

    // Trigger async duration fetch for items missing duration (in background)
    this.enrichMissingDurations(uniqueItems.slice(0, 30));

    return uniqueItems;
  }

  /**
   * Background duration enrichment in parallel batches
   */
  async enrichMissingDurations(items) {
    const missing = items.filter((item) => !item.duration && !store.getVideoDuration(item.videoId));
    if (missing.length === 0) return;

    let hasUpdates = false;
    const chunkSize = 2;
    for (let i = 0; i < missing.length; i += chunkSize) {
      if (i > 0) await new Promise((r) => setTimeout(r, 350));
      const chunk = missing.slice(i, i + chunkSize);
      await Promise.allSettled(
        chunk.map(async (item) => {
          try {
            const res = await fetch(`/api/duration?videoId=${item.videoId}`, {
              signal: AbortSignal.timeout(8000),
            });
            if (res.ok) {
              const data = await res.json();
              if (data && data.duration && data.duration > 0) {
                item.duration = data.duration;
                const isShort = item.isShort || data.duration < SHORT_MAX_SECONDS;
                item.isShort = isShort;
                store.saveVideoDuration(item.videoId, data.duration, false);
                hasUpdates = true;

                const formatted = formatDuration(data.duration);
                const badge = document.getElementById(`badge-dur-${item.videoId}`);
                const tag = document.getElementById(`tag-dur-${item.videoId}`);
                if (badge) {
                  badge.textContent = formatted || (isShort ? "⚡ Short" : "🎙️ Episodio");
                  if (isShort) {
                    badge.classList.add("badge-short");
                  } else {
                    badge.classList.remove("badge-short");
                  }
                }
                if (tag) {
                  tag.textContent = `⏱️ ${formatted || (isShort ? "Short" : "Episodio")}`;
                  if (isShort) {
                    tag.classList.add("is-short");
                  } else {
                    tag.classList.remove("is-short");
                  }
                }
              }
            }
          } catch (_err) {}
        })
      );
    }

    if (hasUpdates) {
      store.saveDurations();
    }
  }

  /**
   * Resolve a channel input (e.g. @handle or URL) to ID
   */
  async resolveChannel(input) {
    try {
      let res = await fetch(`/api/resolve-handle?handle=${encodeURIComponent(input)}`);
      if (!res.ok) {
        res = await fetch(`/api/resolve?q=${encodeURIComponent(input)}`);
      }
      if (res.ok) {
        return await res.json();
      }
    } catch {}
    return null;
  }
}

export const feedService = new FeedService();
